import { Theme } from '@mui/material/styles';
import { TooltipItem } from 'chart.js';
const CHART_FONT =
  'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif';

// Truncate long text with ellipsis (for chart y-axis labels)
const truncateWithEllipsis = (text: string, maxLength: number = 42): string => {
  if (!text) return text;
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 3).trim() + '...';
};

// Common configurations
const getGridStyle = (theme: Theme, opacity = 0.08) => ({
  color:
    theme.palette.mode === 'dark'
      ? `rgba(255, 255, 255, ${opacity})`
      : `rgba(0, 0, 0, ${opacity})`,
  lineWidth: opacity < 0.08 ? 0.75 : 0.5,
  drawTicks: false,
  ...(opacity < 0.08 && { borderDash: [6, 6] }),
});

const getTickStyle = (theme: Theme, color?: string) => ({
  color: color || theme.palette.text.secondary,
  font: { size: 12, family: CHART_FONT },
  padding: 3,
});

const getTooltipConfig = (theme: Theme) => ({
  enabled: true,
  backgroundColor: theme.palette.background.paper,
  titleColor: theme.palette.text.primary,
  bodyColor: theme.palette.text.secondary,
  borderColor: theme.palette.divider,
  borderWidth: 1,
  cornerRadius: 6,
  padding: 8,
  mode: 'point' as const,
  intersect: true,
  callbacks: {
    label: function (context: TooltipItem<'bar'>) {
      const datasetLabel = context.dataset.label?.split(' ')[0];
      return `${datasetLabel}: ${context.parsed.y}`;
    },
  },
});

// Base chart configuration
const getBaseConfig = (theme: Theme, isHorizontal = false) => ({
  responsive: true,
  maintainAspectRatio: false,
  indexAxis: isHorizontal ? ('y' as const) : ('x' as const),
  animation: { duration: 400, easing: 'easeOutQuart' as const },
  elements: { bar: { borderRadius: 0, borderSkipped: false } },
  plugins: { tooltip: getTooltipConfig(theme) },
});

// Main search chart (vertical stacked bars)
export const createSearchChartOptions = (theme: Theme) => ({
  ...getBaseConfig(theme),
  layout: { padding: { top: 10, bottom: 5 } },
  plugins: {
    ...getBaseConfig(theme).plugins,
    tooltip: {
      ...getTooltipConfig(theme),
      mode: 'index' as const,
      intersect: false,
    },
    legend: {
      display: true,
      position: 'bottom' as const,
      labels: {
        padding: 10,
        usePointStyle: true,
        pointStyle: 'rect',
        color: theme.palette.text.secondary,
        font: { size: 13, family: CHART_FONT },
        boxWidth: 20,
        boxHeight: 12,
      },
    },
    datalabels: { display: false },
  },
  scales: {
    x: {
      stacked: true,
      grid: { display: false },
      border: { display: false },
      ticks: { ...getTickStyle(theme, theme.palette.text.primary) },
    },
    y: {
      stacked: true,
      beginAtZero: true,
      grid: getGridStyle(theme),
      border: { display: false },
      ticks: {
        ...getTickStyle(theme),
        precision: 0,
        callback: (v: any) => v.toLocaleString(),
      },
    },
  },
});

// Top Searches chart - grouped horizontal bars with dual X axes
export const createTopSearchesChartOptions = (theme: Theme) => {
  // Detect zoom level to adjust padding
  const zoomLevel = window.devicePixelRatio || 1;
  const rightPadding = zoomLevel >= 1.5 ? 50 : zoomLevel >= 1.2 ? 40 : 35;
  const leftPadding = zoomLevel >= 1.5 ? 20 : 15;

  return {
    ...getBaseConfig(theme, true),
    layout: {
      padding: { right: rightPadding, left: leftPadding, top: 25, bottom: 25 },
    },
    datasets: {
      bar: {
        categoryPercentage: 0.75, // Same as agreements for consistency
        barPercentage: 0.9, // Same as agreements for consistency
      },
    },
    interaction: {
      mode: 'point' as const,
      intersect: true,
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        backgroundColor: theme.palette.background.paper,
        titleColor: theme.palette.text.primary,
        bodyColor: theme.palette.text.secondary,
        borderColor: theme.palette.divider,
        borderWidth: 1,
        cornerRadius: 6,
        padding: 8,
        displayColors: true,
        mode: 'point' as const,
        intersect: true,
        callbacks: {
          label: function (context: any) {
            const label = context.dataset.label || '';
            const value = context.raw || 0;
            return `${label}: ${value.toLocaleString()}`;
          },
        },
      },
      datalabels: {
        display: true,
        anchor: 'end' as const,
        align: 'end' as const,
        offset: 4,
        color: theme.palette.text.secondary,
        font: { size: 11, family: CHART_FONT },
        formatter: (value: number) => (value > 0 ? value.toLocaleString() : ''),
        clamp: true,
      },
    },
    scales: {
      x: {
        type: 'linear' as const,
        position: 'top' as const,
        beginAtZero: true,
        grid: { display: false },
        border: { display: false },
        ticks: {
          ...getTickStyle(theme, theme.palette.primary.main),
          callback: (v: any) => v.toLocaleString(),
          precision: 0,
        },
        title: {
          display: true,
          text: '■ Searches',
          color: theme.palette.primary.main,
          align: 'end' as const,
          font: { size: 13, weight: '600', family: CHART_FONT },
        },
      },
      x1: {
        type: 'linear' as const,
        position: 'bottom' as const,
        beginAtZero: true,
        grid: getGridStyle(theme, 0.05),
        border: { display: false },
        ticks: {
          ...getTickStyle(theme, theme.palette.secondary.main),
          callback: (v: any) => v.toLocaleString(),
          precision: 0,
        },
        title: {
          display: true,
          text: '■ Results',
          color: theme.palette.secondary.main,
          align: 'end' as const,
          font: { size: 13, weight: '600', family: CHART_FONT },
        },
      },
      y: {
        grid: { display: false },
        border: { display: false },
        ticks: {
          ...getTickStyle(theme, theme.palette.text.primary),
          padding: 10, // Same padding as agreements
          autoSkip: false,
          maxRotation: 0,
          minRotation: 0,
          font: {
            size: 13, // Bigger font size
            lineHeight: 1.3, // Increased line height for better readability
          },
          callback: function (this: any, value: any): string {
            const label = this.getLabelForValue(value);
            if (typeof label === 'string') {
              return truncateWithEllipsis(label, 45);
            }
            return String(label ?? '');
          },
        },
        afterFit: function (scaleInstance: any) {
          // Dynamically set label width based on zoom and available space
          const zoomLevel = window.devicePixelRatio || 1;
          const baseWidth = zoomLevel >= 1.5 ? 240 : 200;
          // Limit width to 35% of chart width to prevent overflow
          const maxWidth = Math.min(
            baseWidth,
            scaleInstance.chart.width * 0.35
          );
          scaleInstance.width = Math.max(scaleInstance.width, maxWidth);
        },
      },
    },
  };
};

// Agreements chart - grouped horizontal bars with multi-line labels
export const createAgreementsChartOptions = (theme: Theme) => {
  // Detect zoom level to adjust padding
  const zoomLevel = window.devicePixelRatio || 1;
  const rightPadding = zoomLevel >= 1.5 ? 50 : zoomLevel >= 1.2 ? 40 : 35;
  const leftPadding = zoomLevel >= 1.5 ? 20 : 15;

  return {
    ...getBaseConfig(theme, true),
    layout: {
      padding: { right: rightPadding, left: leftPadding, top: 25, bottom: 25 },
    },
    datasets: {
      bar: {
        categoryPercentage: 0.75, // Less space between categories for tighter grouping
        barPercentage: 0.9, // Thicker bars within each category
      },
    },
    interaction: {
      mode: 'point' as const,
      intersect: true,
    },
    plugins: {
      legend: {
        display: true,
        position: 'bottom' as const,
        labels: {
          padding: 10,
          usePointStyle: true,
          pointStyle: 'rect',
          font: { size: 13, family: CHART_FONT },
          color: theme.palette.text.secondary,
          boxWidth: 20,
          boxHeight: 12,
        },
      },
      tooltip: {
        enabled: true,
        backgroundColor: theme.palette.background.paper,
        titleColor: theme.palette.text.primary,
        bodyColor: theme.palette.text.secondary,
        borderColor: theme.palette.divider,
        borderWidth: 1,
        cornerRadius: 6,
        padding: 8,
        displayColors: true,
        mode: 'point' as const,
        intersect: true,
        callbacks: {
          label: function (context: any) {
            const label = context.dataset.label || '';
            const value = context.raw || 0;
            return `${label}: ${value.toLocaleString()}`;
          },
        },
      },
      datalabels: {
        display: true,
        anchor: 'end' as const,
        align: 'end' as const,
        offset: 4,
        color: theme.palette.text.secondary,
        font: { size: 11, family: CHART_FONT },
        formatter: (value: number) => (value > 0 ? value.toLocaleString() : ''),
        clamp: true,
      },
    },
    scales: {
      x: {
        stacked: false,
        beginAtZero: true,
        grid: getGridStyle(theme),
        border: { display: false },
        ticks: {
          ...getTickStyle(theme),
          callback: (v: any) => v.toLocaleString(),
        },
      },
      y: {
        stacked: false,
        grid: { display: false },
        border: { display: false },
        ticks: {
          ...getTickStyle(theme, theme.palette.text.primary),
          padding: 10, // More padding between labels and chart
          autoSkip: false,
          maxRotation: 0,
          minRotation: 0,
          font: {
            size: 13, // Bigger font size
            lineHeight: 1, // Normal line height
          },
          callback: function (this: any, value: any): string {
            const label = this.getLabelForValue(value);
            if (typeof label === 'string') {
              return truncateWithEllipsis(label, 45);
            }
            return String(label ?? '');
          },
        },
        afterFit: function (scaleInstance: any) {
          // Dynamically set label width based on zoom and available space
          const zoomLevel = window.devicePixelRatio || 1;
          const baseWidth = zoomLevel >= 1.5 ? 240 : 200;
          // Limit width to 35% of chart width to prevent overflow
          const maxWidth = Math.min(
            baseWidth,
            scaleInstance.chart.width * 0.35
          );
          scaleInstance.width = Math.max(scaleInstance.width, maxWidth);
        },
      },
    },
  };
};

// Simple horizontal bar charts (No Results, Filters, Search Filters)
export const createSimpleHorizontalChartOptions = (theme: Theme) => ({
  ...getBaseConfig(theme, true),
  layout: {
    padding: {
      right: 50,
      left: 15,
      top: 20,
      bottom: 20,
    },
  },
  datasets: {
    bar: {
      categoryPercentage: 0.8, // Consistent bar spacing
      barPercentage: 0.85, // Consistent bar thickness
    },
  },
  interaction: {
    mode: 'point' as const,
    intersect: true,
  },
  plugins: {
    legend: {
      display: false, // No legend for single dataset charts
    },
    tooltip: {
      enabled: true,
      backgroundColor: theme.palette.background.paper,
      titleColor: theme.palette.text.primary,
      bodyColor: theme.palette.text.secondary,
      borderColor: theme.palette.divider,
      borderWidth: 1,
      cornerRadius: 6,
      padding: 8,
      displayColors: false, // No color box for single dataset
      mode: 'point' as const,
      intersect: true,
      callbacks: {
        label: function (context: any) {
          const value = context.raw || 0;
          return value.toLocaleString();
        },
      },
    },
    datalabels: {
      display: true,
      anchor: 'end' as const,
      align: 'end' as const,
      offset: 6,
      color: theme.palette.text.secondary,
      font: {
        size: 12,
        family: CHART_FONT,
        weight: '500',
      },
      formatter: (value: number) => (value > 0 ? value.toLocaleString() : ''),
      clamp: true,
    },
  },
  scales: {
    x: {
      beginAtZero: true,
      grid: getGridStyle(theme, 0.06),
      border: { display: false },
      ticks: {
        ...getTickStyle(theme),
        callback: (v: any) => v.toLocaleString(),
        precision: 0,
      },
    },
    y: {
      grid: { display: false },
      border: { display: false },
      ticks: {
        ...getTickStyle(theme, theme.palette.text.primary),
        padding: 8,
        autoSkip: false,
        maxRotation: 0,
        minRotation: 0,
        font: {
          size: 13,
          family: CHART_FONT,
        },
        callback: function (this: any, value: any): string {
          const label = this.getLabelForValue(value);
          if (typeof label === 'string') {
            return truncateWithEllipsis(label, 45);
          }
          return String(label ?? '');
        },
      },
      afterFit: function (scaleInstance: any) {
        // Ensure consistent label width
        const minWidth = 150;
        scaleInstance.width = Math.max(scaleInstance.width, minWidth);
      },
    },
  },
});

// Account Activity chart - grouped horizontal bars with multi-line labels
export const createAccountActivityChartOptions = (theme: Theme) => ({
  ...getBaseConfig(theme, true),
  layout: { padding: { right: 50, left: 10, top: 8, bottom: 8 } },
  datasets: {
    bar: {
      categoryPercentage: 0.7, // Less space for bars, more for gaps (consistent with other charts)
      barPercentage: 0.85, // Slightly thinner bars within category
    },
  },
  interaction: {
    mode: 'point' as const,
    intersect: true,
  },
  plugins: {
    legend: {
      display: true,
      position: 'bottom' as const,
      labels: {
        padding: 10,
        usePointStyle: true,
        pointStyle: 'rect',
        font: { size: 13, family: CHART_FONT },
        color: theme.palette.text.secondary,
        boxWidth: 20,
        boxHeight: 12,
      },
    },
    tooltip: {
      enabled: true,
      backgroundColor: theme.palette.background.paper,
      titleColor: theme.palette.text.primary,
      bodyColor: theme.palette.text.secondary,
      borderColor: theme.palette.divider,
      borderWidth: 1,
      cornerRadius: 6,
      padding: 8,
      displayColors: true,
      mode: 'point' as const,
      intersect: true,
      callbacks: {
        label: function (context: any) {
          const label = context.dataset.label || '';
          const value = context.raw || 0;
          return `${label}: ${value.toLocaleString()}`;
        },
      },
    },
    datalabels: {
      display: true,
      anchor: 'end' as const,
      align: 'end' as const,
      offset: 4,
      color: theme.palette.text.secondary,
      font: { size: 11, family: CHART_FONT },
      formatter: (value: number) => (value > 0 ? value.toLocaleString() : ''),
      clamp: true,
    },
  },
  scales: {
    x: {
      stacked: false,
      beginAtZero: true,
      grid: getGridStyle(theme),
      border: { display: false },
      ticks: {
        ...getTickStyle(theme),
        callback: (v: any) => v.toLocaleString(),
      },
    },
    y: {
      stacked: false,
      grid: { display: false },
      border: { display: false },
      ticks: {
        ...getTickStyle(theme, theme.palette.text.primary),
        padding: 4,
        autoSkip: false,
        maxRotation: 0,
        minRotation: 0,
        callback: function (this: any, value: any): string {
          const label = this.getLabelForValue(value);
          if (typeof label === 'string') {
            return truncateWithEllipsis(label, 45);
          }
          return String(label ?? '');
        },
      },
    },
  },
});
